import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColDef } from 'ag-grid-community';
import { ArrowLeft, Loader2, ScrollText, Package, FileText, Paperclip, MessageSquare, History, Upload, Trash2, Download, MoreHorizontal, User, Sparkles, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
} from '@/components/ui/select';
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from '@/components/ui/resizable';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast as sonnerToast } from 'sonner';
import { QuoteSourceDocuments } from '@/components/quote/QuoteSourceDocuments';
import { ResponsiveSplit } from '@/components/layout/ResponsiveSplit';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  quoteService,
  profileService,
  attachmentService,
  documentChunkService,
  type QuoteDetailRow,
  type QuoteVersionRow,
  type QuoteItemRow,
  type ServiceContext,
} from '@/services';
import type { Attachment } from '@/services';
import VectorizeStatusBadge from '@/components/pricebook/VectorizeStatusBadge';
import ChunkViewerDialog from '@/components/pricebook/ChunkViewerDialog';
import VectorizationLogsDialog from '@/components/pricebook/VectorizationLogsDialog';
import { EntityStatusActions } from '@/components/status/EntityStatusActions';
import { usePagination } from '@/hooks/usePagination';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { parseISO, format } from 'date-fns';

export default function QuoteDetail() {
  const { id: quoteId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const [selectedAttachments, setSelectedAttachments] = useState<Set<string>>(new Set());
  const [uploadEntityType, setUploadEntityType] = useState<'quote' | 'quote_version'>('quote');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [vectorizingAttachmentId, setVectorizingAttachmentId] = useState<string | null>(null);
  const [chunkViewerAttachment, setChunkViewerAttachment] = useState<{ id: string; name: string; entityType: string; entityId: string } | null>(null);
  const [logsAttachment, setLogsAttachment] = useState<{ id: string; name: string } | null>(null);

  // Pagination for items grid
  const itemsPagination = usePagination({
    storageKey: 'quote_detail_items',
    persistToUrl: false,
  });

  // Fetch user profile for company_id
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  // Fetch quote detail
  const { data: quote, isLoading: isLoadingQuote } = useQuery({
    queryKey: ['quote', quoteId],
    queryFn: async () => {
      const result = await quoteService.getQuoteDetail(quoteId!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!quoteId,
  });

  // Fetch versions
  const { data: versions = [], isLoading: isLoadingVersions } = useQuery({
    queryKey: ['quote-versions', quoteId],
    queryFn: async () => {
      const result = await quoteService.listVersionsByQuote(quoteId!);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!quoteId,
  });

  // Auto-select latest version on load
  useEffect(() => {
    if (versions.length > 0 && !selectedVersionId) {
      setSelectedVersionId(versions[0].id);
    }
  }, [versions, selectedVersionId]);

  // Get the currently selected version object
  const selectedVersion = useMemo(
    () => versions.find((v) => v.id === selectedVersionId) ?? null,
    [versions, selectedVersionId]
  );

  // Fetch paginated items for selected version
  const { data: itemsResult, isLoading: isLoadingItems, isFetching: isFetchingItems } = useQuery({
    queryKey: [
      'quote-version-items',
      selectedVersionId,
      itemsPagination.pageIndex,
      itemsPagination.pageSize,
      itemsPagination.debouncedSearch,
    ],
    queryFn: async () => {
      const result = await quoteService.listItemsByVersionPaginated(
        selectedVersionId!,
        { page: itemsPagination.pageIndex, pageSize: itemsPagination.pageSize },
        { search: itemsPagination.debouncedSearch || undefined }
      );
      if (result.error) throw result.error;
      return result;
    },
    enabled: !!selectedVersionId,
  });

  // Fetch attachments for quote + all versions
  const versionIds = useMemo(() => versions.map((v) => v.id), [versions]);
  const { data: attachments = [], isLoading: attachmentsLoading, refetch: refetchAttachments } = useQuery({
    queryKey: ['quote-attachments', quoteId, versionIds],
    queryFn: async () => {
      const result = await attachmentService.listByEntities([
        { entityType: 'quote', entityIds: [quoteId!] },
        { entityType: 'quote_version', entityIds: versionIds },
      ]);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!quoteId,
  });

  // Query for chunk counts per attachment
  const attachmentIds = attachments.map(a => a.id);
  const { data: chunkCounts = {} } = useQuery({
    queryKey: ['attachment-chunk-counts', attachmentIds],
    queryFn: async () => {
      const result = await documentChunkService.getChunkCountsByAttachments(attachmentIds);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: attachmentIds.length > 0,
  });

  // Status update mutation
  const updateVersionStatus = useMutation({
    mutationFn: async ({ statusId, comments }: { statusId: string; comments?: string }) => {
      if (!selectedVersion || !profile?.company_id || !user) throw new Error('Missing required data');

      const context: ServiceContext = {
        companyId: profile.company_id,
        userId: user.id,
      };

      const result = await quoteService.updateQuoteVersionStatus(
        selectedVersion.id,
        statusId,
        selectedVersion.status_id,
        context,
        { comments }
      );

      if (result.error) throw result.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quote-versions', quoteId] });
      toast({ title: 'Status updated', description: 'Quote version status has been updated.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update status.', variant: 'destructive' });
    },
  });

  // Handle version change
  const handleVersionChange = (value: string) => {
    if (value === '__upload_new_version__') {
      navigate(`/quotes/upload?quoteId=${quoteId}`);
      return; // Don't update selectedVersionId state
    }
    setSelectedVersionId(value);
    itemsPagination.resetToFirstPage();
  };

  // Attachment handlers
  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    const { url, error } = await attachmentService.getSignedUrl(attachment.file_path);
    if (error || !url) {
      sonnerToast.error('Failed to generate download link');
      return;
    }
    window.open(url, '_blank');
  };

  const handleDeleteAttachment = async (attachment: Attachment) => {
    if (!profile?.company_id || !user?.id) return;
    setDeletingAttachmentId(attachment.id);
    try {
      const context: ServiceContext = { companyId: profile.company_id, userId: user.id };
      const result = await attachmentService.delete(
        attachment.id,
        attachment.file_path,
        attachment as unknown as Record<string, unknown>,
        context
      );
      if (result.error) {
        sonnerToast.error('Failed to delete attachment');
        return;
      }
      sonnerToast.success('Attachment deleted');
      refetchAttachments();
    } catch {
      sonnerToast.error('Failed to delete attachment');
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const handleToggleAttachmentSelection = (attachmentId: string) => {
    setSelectedAttachments((prev) => {
      const next = new Set(prev);
      if (next.has(attachmentId)) {
        next.delete(attachmentId);
      } else {
        next.add(attachmentId);
      }
      return next;
    });
  };

  const handleToggleAllAttachments = (checked: boolean) => {
    if (checked) {
      setSelectedAttachments(new Set(attachments.map((a) => a.id)));
    } else {
      setSelectedAttachments(new Set());
    }
  };

  const resetUploadForm = () => {
    setUploadFile(null);
    setUploadNotes('');
    setUploadEntityType('quote');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const selectedAttachmentIsPdf = (() => {
    if (selectedAttachments.size !== 1) return false;
    const selectedId = Array.from(selectedAttachments)[0];
    const attachment = attachments.find(a => a.id === selectedId);
    return attachment?.mime_type?.includes('pdf') ?? false;
  })();

  const handleVectorizeAttachment = async () => {
    if (!profile?.company_id || !user?.id) return;
    const selectedId = Array.from(selectedAttachments)[0];
    if (!selectedId) return;

    const attachment = attachments.find(a => a.id === selectedId);
    if (!attachment || !attachment.mime_type?.includes('pdf')) {
      sonnerToast.error('Only PDF files can be vectorized');
      return;
    }

    setVectorizingAttachmentId(selectedId);
    try {
      const context: ServiceContext = { companyId: profile.company_id, userId: user.id };
      const result = await documentChunkService.vectorizeDocument(
        selectedId,
        attachment.entity_type,
        attachment.entity_id,
        context
      );

      if (result.error) {
        sonnerToast.error(`Vectorization failed: ${result.error.message}`);
        return;
      }

      sonnerToast.success(`Document vectorized: ${result.data?.chunks_created} chunks created`);
      queryClient.invalidateQueries({ queryKey: ['attachment-chunk-counts'] });
      queryClient.invalidateQueries({ queryKey: ['vectorization-logs'] });
    } catch {
      sonnerToast.error('Failed to vectorize document');
    } finally {
      setVectorizingAttachmentId(null);
    }
  };

  const handleDeleteChunks = async (attachmentId: string) => {
    if (!profile?.company_id || !user?.id) return;
    const context: ServiceContext = { companyId: profile.company_id, userId: user.id };
    const result = await documentChunkService.deleteByAttachment(attachmentId, context);
    if (result.error) {
      sonnerToast.error('Failed to delete chunks');
      return;
    }
    sonnerToast.success('Chunks deleted');
    queryClient.invalidateQueries({ queryKey: ['attachment-chunk-counts'] });
    setChunkViewerAttachment(null);
  };

  const handleUploadAttachment = async () => {
    if (!uploadFile || !profile?.company_id || !user?.id) return;

    const entityType = uploadEntityType;
    const entityId = entityType === 'quote' ? quoteId! : selectedVersionId!;

    setIsUploading(true);
    try {
      const context: ServiceContext = { companyId: profile.company_id, userId: user.id };
      const result = await attachmentService.create(uploadFile, entityType, entityId, context, {
        notes: uploadNotes.trim() || undefined,
      });

      if (result.error) {
        sonnerToast.error(result.error.message);
        return;
      }

      sonnerToast.success('Attachment uploaded successfully');
      resetUploadForm();
      setUploadDialogOpen(false);
      refetchAttachments();
    } catch (err) {
      console.error(err);
      sonnerToast.error('Failed to upload attachment');
    } finally {
      setIsUploading(false);
    }
  };

  // Notes truncation
  const NOTES_WORD_LIMIT = 50;
  const truncateWords = (text: string, limit: number) => {
    const words = text.split(/\s+/);
    if (words.length <= limit) return null;
    return words.slice(0, limit).join(' ') + '...';
  };

  // Currency formatter
  const formatCurrency = (value: number | null) => {
    if (value == null) return '\u2014';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  // Date formatter
  const formatDate = (value: string | null) => {
    if (!value) return '\u2014';
    try {
      return format(parseISO(value), 'MM/dd/yyyy');
    } catch {
      return '\u2014';
    }
  };

  // Items grid column definitions
  const itemColumnDefs = useMemo<ColDef<QuoteItemRow>[]>(() => [
    {
      headerName: '#',
      field: 'sequence',
      width: 70,
      type: 'rightAligned',
      valueFormatter: (p) => p.value != null ? String(p.value) : '\u2014',
    },
    {
      headerName: 'Item Code',
      field: 'vendor_itemcode',
      flex: 1,
      valueFormatter: (p) => p.value || '\u2014',
    },
    {
      headerName: 'Description',
      field: 'vendor_short_description',
      flex: 2,
      valueFormatter: (p) => p.value || '\u2014',
    },
    {
      headerName: 'UOM',
      field: 'vendor_uom',
      width: 100,
      valueFormatter: (p) => p.value || '\u2014',
    },
    {
      headerName: 'Qty',
      field: 'vendor_quantity',
      width: 100,
      type: 'rightAligned',
      valueFormatter: (p) => p.value != null ? String(p.value) : '\u2014',
    },
    {
      headerName: 'Unit Price',
      field: 'vendor_unit_price',
      width: 130,
      type: 'rightAligned',
      valueFormatter: (p) => formatCurrency(p.value),
    },
    {
      headerName: 'Total Price',
      field: 'vendor_price',
      width: 130,
      type: 'rightAligned',
      valueFormatter: (p) => formatCurrency(p.value),
    },
  ], []);

  // Loading state
  if (isLoadingQuote || isLoadingVersions) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Not found
  if (!quote) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Quote not found.</p>
        <Button variant="outline" onClick={() => navigate('/quotes')}>
          Back to Quotes
        </Button>
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/quotes')}
        className="mb-4 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Quotes
      </Button>

      <ResponsiveSplit
        mobile={
          <div className="space-y-6">
        {/* Header + Status/Version panel */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left panel - 75% */}
          <div className="bg-card border border-border rounded-lg p-6 lg:w-[75%]">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <ScrollText className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  {quote.vendor?.name} &mdash; Quote
                </h1>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-sm text-muted-foreground">
                  <div>
                    <span className="font-medium text-foreground">Well Name:</span>{' '}
                    {quote.well_name || '\u2014'}
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Well API:</span>{' '}
                    {quote.well_api || '\u2014'}
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Project:</span>{' '}
                    {quote.project?.name || '\u2014'}
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Quote #:</span>{' '}
                    {selectedVersion?.vendor_quote_number || '\u2014'}
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Effective:</span>{' '}
                    {formatDate(selectedVersion?.effective_date ?? null)}
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Expires:</span>{' '}
                    {formatDate(selectedVersion?.expiration_date ?? null)}
                  </div>
                </div>

                {selectedVersion?.vendor_notes && (
                  <p className="text-sm text-muted-foreground mt-2 italic">
                    {truncateWords(selectedVersion.vendor_notes, NOTES_WORD_LIMIT) && !notesExpanded
                      ? truncateWords(selectedVersion.vendor_notes, NOTES_WORD_LIMIT)
                      : selectedVersion.vendor_notes}
                    {truncateWords(selectedVersion.vendor_notes, NOTES_WORD_LIMIT) && (
                      <button
                        type="button"
                        onClick={() => setNotesExpanded(!notesExpanded)}
                        className="ml-1 text-primary hover:underline font-medium not-italic"
                      >
                        {notesExpanded ? 'show less' : 'show more'}
                      </button>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right panel - 25% */}
          <div className="bg-card border border-border rounded-lg p-6 lg:w-[25%] flex flex-col">
            {/* Status actions */}
            <EntityStatusActions
              entityType="quote_version"
              currentStatusId={selectedVersion?.status_id ?? null}
              onStatusChange={(statusId, _statusName, comments) =>
                updateVersionStatus.mutate({ statusId, comments })
              }
            />

            {/* Version selector pushed to bottom */}
            <div className="mt-auto pt-4">
              <label className="text-sm font-medium text-foreground mb-1 block">Version</label>
              <Select
                value={selectedVersionId ?? ''}
                onValueChange={handleVersionChange}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select version" />
                </SelectTrigger>
                <SelectContent>
                  {/* Special upload action - always first */}
                  <SelectItem
                    value="__upload_new_version__"
                    className="text-primary font-medium"
                  >
                    <Upload className="w-4 h-4 inline mr-2" />
                    Upload New Version
                  </SelectItem>

                  <SelectSeparator />

                  {/* Existing versions */}
                  {versions.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      v{v.version_number} &mdash; {v.status?.name || 'No Status'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="items">
          <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-6">
            <TabsTrigger
              value="items"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2"
            >
              <Package className="w-4 h-4" />
              Items
            </TabsTrigger>
            <TabsTrigger
              value="invoices"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              Invoices
            </TabsTrigger>
            <TabsTrigger
              value="attachments"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2"
            >
              <Paperclip className="w-4 h-4" />
              Attachments
            </TabsTrigger>
            <TabsTrigger
              value="notes"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Notes
            </TabsTrigger>
            <TabsTrigger
              value="audit"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2"
            >
              <History className="w-4 h-4" />
              Audit
            </TabsTrigger>
          </TabsList>

          {/* Items Tab */}
          <TabsContent value="items" className="mt-4">
            <AGDataGrid<QuoteItemRow>
              rowData={itemsResult?.data ?? []}
              columnDefs={itemColumnDefs}
              totalCount={itemsResult?.totalCount ?? 0}
              pageIndex={itemsPagination.pageIndex}
              pageSize={itemsPagination.pageSize}
              pageSizeOptions={itemsPagination.pageSizeOptions}
              searchValue={itemsPagination.searchValue}
              searchPlaceholder="Search item code or description..."
              isLoading={isLoadingItems}
              isFetching={isFetchingItems}
              onSearchChange={itemsPagination.setSearchValue}
              onPageChange={itemsPagination.setPageIndex}
              onPageSizeChange={itemsPagination.setPageSize}
              height="calc(100vh - 520px)"
            />
          </TabsContent>

          {/* Placeholder tabs */}
          <TabsContent value="invoices" className="mt-4">
            <div className="text-center py-12 text-muted-foreground">
              Invoices tab coming soon.
            </div>
          </TabsContent>
          <TabsContent value="attachments" className="mt-4">
            <div className="flex items-center justify-end mb-4 gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" disabled={selectedAttachments.size === 0}>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Actions
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={!selectedAttachmentIsPdf || vectorizingAttachmentId !== null}
                    onClick={handleVectorizeAttachment}
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {vectorizingAttachmentId ? 'Vectorizing...' : 'Vectorize Document'}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button onClick={() => setUploadDialogOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Attachment
              </Button>
            </div>
            <div className="bg-card border border-border rounded-lg overflow-hidden">
              {attachmentsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : attachments.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  No attachments have been uploaded yet.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={selectedAttachments.size === attachments.length && attachments.length > 0}
                          onCheckedChange={(checked) => handleToggleAllAttachments(!!checked)}
                        />
                      </TableHead>
                      <TableHead>File Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-center">Vectorized</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Level</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {attachments.map((attachment) => (
                      <TableRow key={attachment.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedAttachments.has(attachment.id)}
                            onCheckedChange={() => handleToggleAttachmentSelection(attachment.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <button
                            className="flex items-center gap-2 text-primary hover:underline"
                            onClick={() => handleDownloadAttachment(attachment)}
                          >
                            <FileText className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">{attachment.file_name}</span>
                          </button>
                          {attachment.notes && (
                            <p className="text-xs text-muted-foreground mt-1">{attachment.notes}</p>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {attachment.mime_type
                            ? attachment.mime_type.split('/').pop()?.toUpperCase() || attachment.mime_type
                            : '—'}
                        </TableCell>
                        <TableCell className="text-center">
                          <VectorizeStatusBadge
                            chunkCount={chunkCounts[attachment.id] ?? null}
                            isVectorizing={vectorizingAttachmentId === attachment.id}
                            onClick={() => setChunkViewerAttachment({ id: attachment.id, name: attachment.file_name, entityType: attachment.entity_type, entityId: attachment.entity_id })}
                          />
                        </TableCell>
                        <TableCell className="text-sm">{attachment.attachment_role || '—'}</TableCell>
                        <TableCell className="text-sm">
                          {attachment.entity_type === 'quote'
                            ? 'Header'
                            : `Version v${versions.find(v => v.id === attachment.entity_id)?.version_number ?? '?'}`}
                        </TableCell>
                        <TableCell>{formatDateTime(attachment.created)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <User className="w-3 h-3 text-muted-foreground" />
                            {attachment.created_by_profile?.full_name || '—'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handleDownloadAttachment(attachment)}>
                                <Download className="w-4 h-4 mr-2" />
                                Download
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setLogsAttachment({ id: attachment.id, name: attachment.file_name })}>
                                <ScrollText className="w-4 h-4 mr-2" />
                                View Logs
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                disabled={deletingAttachmentId === attachment.id}
                                onClick={() => handleDeleteAttachment(attachment)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                {deletingAttachmentId === attachment.id ? 'Deleting...' : 'Delete'}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
          <TabsContent value="notes" className="mt-4">
            <div className="text-center py-12 text-muted-foreground">
              Notes tab coming soon.
            </div>
          </TabsContent>
          <TabsContent value="audit" className="mt-4">
            <div className="text-center py-12 text-muted-foreground">
              Audit tab coming soon.
            </div>
          </TabsContent>
        </Tabs>

        {/* Source Documents */}
        <QuoteSourceDocuments versionId={selectedVersionId} isVisible />
          </div>
        }
        desktop={
          <>
          <div className="flex flex-col lg:flex-row gap-6 mb-6">
        {/* Left panel - 75% */}
        <div className="bg-card border border-border rounded-lg p-6 lg:w-[75%]">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <ScrollText className="w-7 h-7 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {quote.vendor?.name} &mdash; Quote
              </h1>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 text-sm text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground">Well Name:</span>{' '}
                  {quote.well_name || '\u2014'}
                </div>
                <div>
                  <span className="font-medium text-foreground">Well API:</span>{' '}
                  {quote.well_api || '\u2014'}
                </div>
                <div>
                  <span className="font-medium text-foreground">Project:</span>{' '}
                  {quote.project?.name || '\u2014'}
                </div>
                <div>
                  <span className="font-medium text-foreground">Quote #:</span>{' '}
                  {selectedVersion?.vendor_quote_number || '\u2014'}
                </div>
                <div>
                  <span className="font-medium text-foreground">Effective:</span>{' '}
                  {formatDate(selectedVersion?.effective_date ?? null)}
                </div>
                <div>
                  <span className="font-medium text-foreground">Expires:</span>{' '}
                  {formatDate(selectedVersion?.expiration_date ?? null)}
                </div>
              </div>

              {selectedVersion?.vendor_notes && (
                <p className="text-sm text-muted-foreground mt-2 italic">
                  {truncateWords(selectedVersion.vendor_notes, NOTES_WORD_LIMIT) && !notesExpanded
                    ? truncateWords(selectedVersion.vendor_notes, NOTES_WORD_LIMIT)
                    : selectedVersion.vendor_notes}
                  {truncateWords(selectedVersion.vendor_notes, NOTES_WORD_LIMIT) && (
                    <button
                      type="button"
                      onClick={() => setNotesExpanded(!notesExpanded)}
                      className="ml-1 text-primary hover:underline font-medium not-italic"
                    >
                      {notesExpanded ? 'show less' : 'show more'}
                    </button>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Right panel - 25% */}
        <div className="bg-card border border-border rounded-lg p-6 lg:w-[25%] flex flex-col">
          {/* Status actions */}
          <EntityStatusActions
            entityType="quote_version"
            currentStatusId={selectedVersion?.status_id ?? null}
            onStatusChange={(statusId, _statusName, comments) =>
              updateVersionStatus.mutate({ statusId, comments })
            }
          />

          {/* Version selector pushed to bottom */}
          <div className="mt-auto pt-4">
            <label className="text-sm font-medium text-foreground mb-1 block">Version</label>
            <Select
              value={selectedVersionId ?? ''}
              onValueChange={handleVersionChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select version" />
              </SelectTrigger>
              <SelectContent>
                {/* Special upload action - always first */}
                <SelectItem
                  value="__upload_new_version__"
                  className="text-primary font-medium"
                >
                  <Upload className="w-4 h-4 inline mr-2" />
                  Upload New Version
                </SelectItem>

                <SelectSeparator />

                {/* Existing versions */}
                {versions.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    v{v.version_number} &mdash; {v.status?.name || 'No Status'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <ResizablePanelGroup
        direction="horizontal"
        className="flex min-h-[600px]"
      >
        <ResizablePanel defaultSize={65} minSize={40}>
          <div className="pr-3 h-full overflow-auto">
            {/* Tabs */}
            <Tabs defaultValue="items" className="mb-6">
              <TabsList className="w-full justify-start border-b border-border rounded-none bg-transparent h-auto p-0 mb-6">
                <TabsTrigger
                  value="items"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2"
                >
                  <Package className="w-4 h-4" />
                  Items
                </TabsTrigger>
                <TabsTrigger
                  value="invoices"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Invoices
                </TabsTrigger>
                <TabsTrigger
                  value="attachments"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2"
                >
                  <Paperclip className="w-4 h-4" />
                  Attachments
                </TabsTrigger>
                <TabsTrigger
                  value="notes"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2"
                >
                  <MessageSquare className="w-4 h-4" />
                  Notes
                </TabsTrigger>
                <TabsTrigger
                  value="audit"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-4 py-3 flex items-center gap-2"
                >
                  <History className="w-4 h-4" />
                  Audit
                </TabsTrigger>
              </TabsList>

              {/* Items Tab */}
              <TabsContent value="items" className="mt-4">
                <AGDataGrid<QuoteItemRow>
                  rowData={itemsResult?.data ?? []}
                  columnDefs={itemColumnDefs}
                  totalCount={itemsResult?.totalCount ?? 0}
                  pageIndex={itemsPagination.pageIndex}
                  pageSize={itemsPagination.pageSize}
                  pageSizeOptions={itemsPagination.pageSizeOptions}
                  searchValue={itemsPagination.searchValue}
                  searchPlaceholder="Search item code or description..."
                  isLoading={isLoadingItems}
                  isFetching={isFetchingItems}
                  onSearchChange={itemsPagination.setSearchValue}
                  onPageChange={itemsPagination.setPageIndex}
                  onPageSizeChange={itemsPagination.setPageSize}
                  height="calc(100vh - 520px)"
                />
              </TabsContent>

              {/* Placeholder tabs */}
              <TabsContent value="invoices" className="mt-4">
                <div className="text-center py-12 text-muted-foreground">
                  Invoices tab coming soon.
                </div>
              </TabsContent>
              <TabsContent value="attachments" className="mt-4">
                <div className="flex items-center justify-end mb-4 gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" disabled={selectedAttachments.size === 0}>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Actions
                        <ChevronDown className="w-4 h-4 ml-2" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        disabled={!selectedAttachmentIsPdf || vectorizingAttachmentId !== null}
                        onClick={handleVectorizeAttachment}
                      >
                        <Sparkles className="w-4 h-4 mr-2" />
                        {vectorizingAttachmentId ? 'Vectorizing...' : 'Vectorize Document'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button onClick={() => setUploadDialogOpen(true)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Attachment
                  </Button>
                </div>
                <div className="bg-card border border-border rounded-lg overflow-hidden">
                  {attachmentsLoading ? (
                    <div className="flex items-center justify-center h-32">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                  ) : attachments.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      No attachments have been uploaded yet.
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[40px]">
                            <Checkbox
                              checked={selectedAttachments.size === attachments.length && attachments.length > 0}
                              onCheckedChange={(checked) => handleToggleAllAttachments(!!checked)}
                            />
                          </TableHead>
                          <TableHead>File Name</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead className="text-center">Vectorized</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Level</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Created By</TableHead>
                          <TableHead className="w-[50px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {attachments.map((attachment) => (
                          <TableRow key={attachment.id}>
                            <TableCell>
                              <Checkbox
                                checked={selectedAttachments.has(attachment.id)}
                                onCheckedChange={() => handleToggleAttachmentSelection(attachment.id)}
                              />
                            </TableCell>
                            <TableCell>
                              <button
                                className="flex items-center gap-2 text-primary hover:underline"
                                onClick={() => handleDownloadAttachment(attachment)}
                              >
                                <FileText className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium">{attachment.file_name}</span>
                              </button>
                              {attachment.notes && (
                                <p className="text-xs text-muted-foreground mt-1">{attachment.notes}</p>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">
                              {attachment.mime_type
                                ? attachment.mime_type.split('/').pop()?.toUpperCase() || attachment.mime_type
                                : '—'}
                            </TableCell>
                            <TableCell className="text-center">
                              <VectorizeStatusBadge
                                chunkCount={chunkCounts[attachment.id] ?? null}
                                isVectorizing={vectorizingAttachmentId === attachment.id}
                                onClick={() => setChunkViewerAttachment({ id: attachment.id, name: attachment.file_name, entityType: attachment.entity_type, entityId: attachment.entity_id })}
                              />
                            </TableCell>
                            <TableCell className="text-sm">{attachment.attachment_role || '—'}</TableCell>
                            <TableCell className="text-sm">
                              {attachment.entity_type === 'quote'
                                ? 'Header'
                                : `Version v${versions.find(v => v.id === attachment.entity_id)?.version_number ?? '?'}`}
                            </TableCell>
                            <TableCell>{formatDateTime(attachment.created)}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                <User className="w-3 h-3 text-muted-foreground" />
                                {attachment.created_by_profile?.full_name || '—'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreHorizontal className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleDownloadAttachment(attachment)}>
                                    <Download className="w-4 h-4 mr-2" />
                                    Download
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => setLogsAttachment({ id: attachment.id, name: attachment.file_name })}>
                                    <ScrollText className="w-4 h-4 mr-2" />
                                    View Logs
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    disabled={deletingAttachmentId === attachment.id}
                                    onClick={() => handleDeleteAttachment(attachment)}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    {deletingAttachmentId === attachment.id ? 'Deleting...' : 'Delete'}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="notes" className="mt-4">
                <div className="text-center py-12 text-muted-foreground">
                  Notes tab coming soon.
                </div>
              </TabsContent>
              <TabsContent value="audit" className="mt-4">
                <div className="text-center py-12 text-muted-foreground">
                  Audit tab coming soon.
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={35} minSize={25}>
          <div className="pl-3 h-full">
            <QuoteSourceDocuments versionId={selectedVersionId} isVisible />
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
          </>
        }
      />

      {/* Upload Attachment Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onOpenChange={(value) => {
          if (!value) resetUploadForm();
          setUploadDialogOpen(value);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Attachment</DialogTitle>
            <DialogDescription>Select a file, choose the target entity, and optionally add notes.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Attach to</Label>
              <RadioGroup
                value={uploadEntityType}
                onValueChange={(val) => setUploadEntityType(val as 'quote' | 'quote_version')}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="quote" id="upload-quote" />
                  <Label htmlFor="upload-quote" className="font-normal cursor-pointer">Quote</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="quote_version" id="upload-version" disabled={!selectedVersionId} />
                  <Label htmlFor="upload-version" className={`font-normal cursor-pointer ${!selectedVersionId ? 'text-muted-foreground' : ''}`}>
                    Quote Version{selectedVersion ? ` (v${selectedVersion.version_number})` : ''}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="quote-attachment-file">File</Label>
              <input
                id="quote-attachment-file"
                ref={fileInputRef}
                type="file"
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="quote-attachment-notes">Notes (optional)</Label>
              <Textarea
                id="quote-attachment-notes"
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="Add any notes about this attachment..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={isUploading}>
              Cancel
            </Button>
            <Button onClick={handleUploadAttachment} disabled={!uploadFile || isUploading}>
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {chunkViewerAttachment && profile?.company_id && user?.id && (
        <ChunkViewerDialog
          open={!!chunkViewerAttachment}
          onOpenChange={(open) => !open && setChunkViewerAttachment(null)}
          attachmentId={chunkViewerAttachment.id}
          attachmentName={chunkViewerAttachment.name}
          sourceEntityType={chunkViewerAttachment.entityType}
          sourceEntityId={chunkViewerAttachment.entityId}
          context={{ companyId: profile.company_id, userId: user.id }}
          onRevectorize={() => {
            setChunkViewerAttachment(null);
            setSelectedAttachments(new Set([chunkViewerAttachment.id]));
            handleVectorizeAttachment();
          }}
          onDeleteChunks={() => handleDeleteChunks(chunkViewerAttachment.id)}
          isVectorizing={vectorizingAttachmentId === chunkViewerAttachment.id}
        />
      )}

      {logsAttachment && (
        <VectorizationLogsDialog
          open={!!logsAttachment}
          onOpenChange={(open) => !open && setLogsAttachment(null)}
          attachmentId={logsAttachment.id}
          attachmentName={logsAttachment.name}
        />
      )}
    </div>
  );
}
